import * as React from 'react';

/**
 * Pages register their heading with the layout that renders it. The title can depend on
 * loaded data (the dashboard appends the selected site), so it is pushed up from the page
 * rather than declared next to the route.
 */
export interface PageTitle {
  /** Feeds the document title. */
  title?: string;
  /** Heading the layout renders above the page; unset when the page wants none. */
  heading?: string;
}

const PageTitleContext = React.createContext<(pageTitle: PageTitle) => void>(() => {});

export const PageTitleProvider = PageTitleContext.Provider;

/**
 * Pass `{ heading: false }` for pages that draw their own heading (or want none) — the
 * document title is still set from `title`.
 */
export const usePageTitle = (title?: string, { heading = true }: { heading?: boolean } = {}) => {
  const setPageTitle = React.useContext(PageTitleContext);
  // Layout effect, not effect: the header renders above the page, so a post-paint update
  // would flash an empty heading on every navigation.
  React.useLayoutEffect(() => {
    setPageTitle({ title, heading: heading ? title : undefined });
    return () => setPageTitle({});
  }, [title, heading, setPageTitle]);
};
